import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Spin } from 'antd';
import { useSearchParams } from 'react-router-dom';
import { fetchProducts } from '../../services/services';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import ProductCard from '../productCard';
import PageCollectionHeader from '../pageCollectionHeader';
import { InfiniteLoader } from '../infiniteLoader/infiniteLoader';
import { useScrollRestoration } from '../../hooks/useScrollRestoration';
import CatalogEmptyState from '../catalogEmptyState/CatalogEmptyState';
import './allProducts.scss';

const TAKE = 12;
const genderLabels: Record<string, string> = {
  womens: "Women's",
  mens: "Men's",
};

const AllProducts = () => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const [fetchingMoreFromSkip, setFetchingMoreFromSkip] = useState<number | null>(null);
  const { status, products, hasMore, skip } = useAppSelector(
    (state) => state.products
  );
  const currentCatalogFilter = useAppSelector(
    (state) => state.products.currentCatalogFilter
  );
  const loading = status === 'loading';
  const isFetchingMore = fetchingMoreFromSkip !== null;
  const selectedGender = searchParams.get('gender') || '';
  const selectedType = searchParams.get('type') || '';
  const genderFilter = selectedGender === 'womens' || selectedGender === 'mens' ? selectedGender : undefined;
  const typeFilter = selectedType || undefined;
  const filterKey = `${selectedGender}:${selectedType}`;
  const hasCurrentCatalogSnapshot = currentCatalogFilter === filterKey && products.length > 0;
  const title = useMemo(() => {
    const genderLabel = genderFilter ? genderLabels[genderFilter] : '';
    return [genderLabel, typeFilter].filter(Boolean).join(' ') || 'All Products';
  }, [genderFilter, typeFilter]);
  const subtitle = genderFilter || typeFilter
    ? 'Products filtered from the shop menu'
    : 'Browse the full jewelry catalog';

  useScrollRestoration(!loading && products.length > 0, `all-products:${filterKey}`);

  const loadMore = useCallback(() => {
    if (loading || isFetchingMore || !hasMore) return;

    setFetchingMoreFromSkip(skip);
    void dispatch(fetchProducts({
      skip,
      take: TAKE,
      gender: genderFilter,
      type: typeFilter,
      filterKey,
    }));
  }, [dispatch, filterKey, genderFilter, hasMore, isFetchingMore, loading, skip, typeFilter]);

  useEffect(() => {
    if (hasCurrentCatalogSnapshot) return;

    dispatch(fetchProducts({
      skip: 0,
      take: TAKE,
      gender: genderFilter,
      type: typeFilter,
      filterKey,
    }));
  }, [dispatch, filterKey, genderFilter, hasCurrentCatalogSnapshot, typeFilter]);

  useEffect(() => {
    setFetchingMoreFromSkip(null);
  }, [filterKey]);

  useEffect(() => {
    if (fetchingMoreFromSkip === null) return;

    if (skip > fetchingMoreFromSkip || !hasMore || status === 'failed') {
      setFetchingMoreFromSkip(null);
    }
  }, [fetchingMoreFromSkip, hasMore, skip, status]);

  return (
    <div className="all-products-page">
      <PageCollectionHeader
        eyebrow="Catalog"
        title={title}
        subtitle={subtitle}
      />

      {status === 'loading' && products.length === 0 ? (
        <div className="all-products-loading-row all-products-loading-row--initial" role="status" aria-live="polite" aria-label="Loading products">
          <Spin size="large" aria-hidden="true" />
        </div>
      ) : status === 'failed' ? (
        <CatalogEmptyState
          role="alert"
          mark="!"
          eyebrow="Catalog unavailable"
          title="We can’t reach the catalog right now."
          message="The product service is not responding. Start the backend and refresh this page to load the catalog."
          primaryAction={{
            label: 'Refresh catalog',
            to: `/products${searchParams.toString() ? `?${searchParams.toString()}` : ''}`,
            reloadDocument: true,
          }}
          secondaryAction={{ label: 'Go home', to: '/' }}
        />
      ) : products.length === 0 ? (
        <CatalogEmptyState
          eyebrow="Catalog is empty"
          title="No products match this view."
          message="The catalog may be updating, or this filter combination does not have active pieces yet. Try the full catalog or start with rings."
        />
      ) : (
        <InfiniteLoader loadMore={loadMore} hasMore={hasMore} loading={loading || isFetchingMore} showButton={false}>
          <div className="all-products-grid">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}

            {isFetchingMore && (
              <div className="all-products-loading-row" role="status" aria-live="polite" aria-label="Loading more products">
                <Spin size="large" aria-hidden="true" />
              </div>
            )}
          </div>
        </InfiniteLoader>
      )}
    </div>
  );
};

export default AllProducts;
